import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ScrollArea } from '../components/ui/scroll-area';
import { ShieldCheck, Briefcase, Calculator, TrendingUp, Check, X, UserPlus, Building2, Info, Eye, FileText, PieChart, FolderOpen, FileBox } from 'lucide-react';
import { ROLES, getInvitableRoles } from './permissions';
import { toast } from 'sonner';

const ROLE_ICONS = {
  admin: ShieldCheck,
  project_manager: Briefcase,
  accountant: Calculator,
  investor: TrendingUp
};

// Feature permissions that can be toggled for investors
const FEATURE_PERMISSIONS = [
  { id: 'view_dashboard', label: 'Dashboard', description: 'View assigned projects overview', icon: Eye, defaultOn: true, locked: true },
  { id: 'view_projects', label: 'Project Details', description: 'View project progress and phases', icon: FolderOpen, defaultOn: true, locked: true },
  { id: 'view_reports', label: 'Reports', description: 'Access draw reports and history', icon: FileText, defaultOn: false },
  { id: 'view_financial_analysis', label: 'Financial Analysis', description: 'View ROI and projections', icon: PieChart, defaultOn: false },
  { id: 'view_project_hub', label: 'Project Hub', description: 'View contractors and documents', icon: Briefcase, defaultOn: false },
  { id: 'view_bank_templates', label: 'Bank Templates', description: 'View lien waiver templates', icon: FileBox, defaultOn: false },
];

export function InviteTeamMemberDialog({ 
  open, 
  onOpenChange, 
  projects = [],
  onInvite,
  isLoading = false 
}) {
  const [email, setEmail] = useState('');
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState(['view_dashboard', 'view_projects']); // Default permissions
  
  const invitableRoles = getInvitableRoles();
  
  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setEmail('');
      setSelectedRoles([]);
      setSelectedProjects([]);
      setSelectedPermissions(['view_dashboard', 'view_projects']); // Reset to defaults
    }
  }, [open]);
  
  // Check if PM or Investor role is selected (requires project assignment)
  const isPMSelected = selectedRoles.includes('project_manager');
  const isInvestorSelected = selectedRoles.includes('investor');
  const needsProjectAssignment = isPMSelected || isInvestorSelected;
  
  // Get the primary selected role for preview (first selected)
  const previewRole = selectedRoles.length > 0 ? ROLES[selectedRoles[0]] : null;
  
  const handleRoleToggle = (roleId) => {
    if (selectedRoles.includes(roleId)) {
      // Removing role
      const newRoles = selectedRoles.filter(r => r !== roleId);
      setSelectedRoles(newRoles);
      // If removing PM or Investor role, clear project assignments if no other project-requiring role remains
      if (roleId === 'project_manager' || roleId === 'investor') {
        const stillNeedsProjects = newRoles.includes('project_manager') || newRoles.includes('investor');
        if (!stillNeedsProjects) {
          setSelectedProjects([]);
        }
      }
    } else {
      // Adding role
      setSelectedRoles([...selectedRoles, roleId]);
    }
  };
  
  const handleProjectToggle = (projectId) => {
    if (selectedProjects.includes(projectId)) {
      setSelectedProjects(selectedProjects.filter(p => p !== projectId));
    } else {
      setSelectedProjects([...selectedProjects, projectId]);
    }
  };
  
  const handleSelectAllProjects = () => {
    if (selectedProjects.length === projects.length) {
      setSelectedProjects([]);
    } else {
      setSelectedProjects(projects.map(p => p.id));
    }
  };
  
  const handlePermissionToggle = (permissionId) => {
    // Don't allow toggling locked permissions
    const perm = FEATURE_PERMISSIONS.find(p => p.id === permissionId);
    if (perm?.locked) return;
    
    if (selectedPermissions.includes(permissionId)) {
      setSelectedPermissions(selectedPermissions.filter(p => p !== permissionId));
    } else {
      setSelectedPermissions([...selectedPermissions, permissionId]);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      toast.error('Please enter an email address');
      return;
    }
    
    if (selectedRoles.length === 0) {
      toast.error('Please select at least one role');
      return;
    }
    
    if (needsProjectAssignment && selectedProjects.length === 0) {
      const roleLabel = isInvestorSelected ? 'Investor' : 'Project Manager';
      toast.error(`Please assign at least one project for ${roleLabel} role`);
      return;
    }
    
    try {
      await onInvite({
        email: email.trim().toLowerCase(),
        roles: selectedRoles,
        assigned_projects: needsProjectAssignment ? selectedProjects : [],
        custom_permissions: isInvestorSelected ? selectedPermissions : [] // Only for investors
      });
      onOpenChange(false);
    } catch (error) {
      // Error handling done in parent
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden p-0">
        <div className="grid grid-cols-1 md:grid-cols-2 h-full">
          {/* Left Side - Form */}
          <div className="p-6 border-r border-slate-200">
            <DialogHeader className="mb-4">
              <DialogTitle className="flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-blue-600" />
                Invite Team Member
              </DialogTitle>
              <DialogDescription>
                Send an invitation to join your team
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Email Input */}
              <div className="space-y-2">
                <Label htmlFor="invite-email" className="text-sm font-medium">
                  Email Address
                </Label>
                <Input
                  id="invite-email"
                  type="email"
                  placeholder="colleague@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-11"
                  data-testid="invite-email-input"
                />
              </div>
              
              {/* Role Selection */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">Select Role(s)</Label>
                <div className="space-y-2">
                  {invitableRoles.map(role => {
                    const Icon = ROLE_ICONS[role.id];
                    const isSelected = selectedRoles.includes(role.id);
                    
                    return (
                      <div
                        key={role.id}
                        className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          isSelected 
                            ? `${role.color.border} ${role.color.bg}` 
                            : 'border-slate-200 hover:border-slate-300 bg-white'
                        }`}
                        onClick={() => handleRoleToggle(role.id)}
                        data-testid={`role-${role.id}`}
                      >
                        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                          isSelected ? 'bg-blue-600 border-blue-600' : 'border-slate-300'
                        }`}>
                          {isSelected && <Check className="w-3 h-3 text-white" />}
                        </div>
                        <Icon className={`w-5 h-5 ${isSelected ? role.color.text : 'text-slate-500'}`} />
                        <div className="flex-1">
                          <p className={`font-medium ${isSelected ? role.color.text : 'text-slate-700'}`}>
                            {role.name}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              {/* Project Assignment (for PM and Investor roles) */}
              {needsProjectAssignment && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium flex items-center gap-1">
                      <Building2 className={`w-4 h-4 ${isInvestorSelected ? 'text-emerald-600' : 'text-blue-600'}`} />
                      {isInvestorSelected ? 'Assign Investment Projects' : 'Assign to Projects'}
                      <span className="text-red-500">*</span>
                    </Label>
                    <button
                      type="button"
                      onClick={handleSelectAllProjects}
                      className={`text-xs font-medium ${isInvestorSelected ? 'text-emerald-600 hover:text-emerald-700' : 'text-blue-600 hover:text-blue-700'}`}
                    >
                      {selectedProjects.length === projects.length ? 'Clear All' : 'Select All'}
                    </button>
                  </div>
                  
                  {isInvestorSelected && (
                    <p className="text-xs text-slate-500 flex items-center gap-1">
                      <Info className="w-3 h-3" />
                      Investor will only see projects assigned below
                    </p>
                  )}
                  
                  <ScrollArea className="h-[140px] border rounded-lg p-2">
                    <div className="space-y-1">
                      {projects.length === 0 ? (
                        <p className="text-sm text-slate-500 text-center py-4">
                          No projects available
                        </p>
                      ) : (
                        projects.map(project => {
                          const isSelected = selectedProjects.includes(project.id);
                          return (
                            <div
                              key={project.id}
                              className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                                isSelected ? 'bg-blue-50' : 'hover:bg-slate-50'
                              }`}
                              onClick={() => handleProjectToggle(project.id)}
                            >
                              <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                                isSelected ? 'bg-blue-600 border-blue-600' : 'border-slate-300'
                              }`}>
                                {isSelected && <Check className="w-3 h-3 text-white" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-slate-700 truncate">
                                  {project.name}
                                </p>
                                <p className="text-xs text-slate-500 truncate">
                                  {project.address}
                                </p>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </ScrollArea>
                  <p className="text-xs text-slate-500 flex items-center gap-1">
                    <Info className="w-3 h-3" />
                    This user will only see and manage selected projects
                  </p>
                </div>
              )}

              {/* Feature Permissions (only for Investors) */}
              {isInvestorSelected && (
                <div className="space-y-3 border-t pt-4">
                  <Label className="text-sm font-medium flex items-center gap-1">
                    <Eye className="w-4 h-4 text-emerald-600" />
                    Feature Access
                  </Label>
                  <p className="text-xs text-slate-500">
                    Choose which features this investor can access
                  </p>
                  
                  <div className="space-y-2">
                    {FEATURE_PERMISSIONS.map(permission => {
                      const Icon = permission.icon;
                      const isSelected = selectedPermissions.includes(permission.id);
                      const isLocked = permission.locked;
                      
                      return (
                        <div
                          key={permission.id}
                          className={`flex items-center gap-3 p-2 rounded-lg border transition-colors ${
                            isLocked 
                              ? 'bg-slate-50 border-slate-200 cursor-not-allowed' 
                              : isSelected 
                                ? 'bg-emerald-50 border-emerald-200 cursor-pointer' 
                                : 'bg-white border-slate-200 hover:border-slate-300 cursor-pointer'
                          }`}
                          onClick={() => !isLocked && handlePermissionToggle(permission.id)}
                        >
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                            isSelected 
                              ? 'bg-emerald-600 border-emerald-600' 
                              : 'border-slate-300'
                          }`}>
                            {isSelected && <Check className="w-3 h-3 text-white" />}
                          </div>
                          <Icon className={`w-4 h-4 flex-shrink-0 ${isSelected ? 'text-emerald-600' : 'text-slate-400'}`} />
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium ${isSelected ? 'text-emerald-700' : 'text-slate-600'}`}>
                              {permission.label}
                              {isLocked && <span className="text-xs text-slate-400 ml-1">(Required)</span>}
                            </p>
                            <p className="text-xs text-slate-500 truncate">{permission.description}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Submit Button */}
              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isLoading || !email || selectedRoles.length === 0 || (isPMSelected && selectedProjects.length === 0)}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  data-testid="send-invite-btn"
                >
                  {isLoading ? 'Sending...' : 'Send Invitation'}
                </Button>
              </div>
            </form>
          </div>
          
          {/* Right Side - Role Preview */}
          <div className="p-6 bg-slate-50 flex flex-col">
            <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">
              Role Preview
            </h3>
            
            {previewRole ? (
              <div className="flex-1">
                {/* Role Header */}
                <div className={`flex items-center gap-3 p-4 rounded-lg ${previewRole.color.bg} ${previewRole.color.border} border-2 mb-4`}>
                  {React.createElement(ROLE_ICONS[previewRole.id], {
                    className: `w-6 h-6 ${previewRole.color.text}`
                  })}
                  <div>
                    <h4 className={`font-bold ${previewRole.color.text}`}>
                      {previewRole.name}
                    </h4>
                    <p className={`text-xs ${previewRole.color.text} opacity-80`}>
                      {previewRole.description}
                    </p>
                  </div>
                </div>
                
                {/* Can Do */}
                <div className="mb-4">
                  <h5 className="text-sm font-semibold text-green-700 flex items-center gap-1 mb-2">
                    <Check className="w-4 h-4" /> CAN DO
                  </h5>
                  <ul className="space-y-1">
                    {previewRole.canDo.map((item, idx) => (
                      <li key={idx} className="text-sm text-slate-600 flex items-start gap-2">
                        <span className="text-green-500 mt-1">•</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                
                {/* Cannot Do */}
                {previewRole.cannotDo.length > 0 && (
                  <div>
                    <h5 className="text-sm font-semibold text-red-700 flex items-center gap-1 mb-2">
                      <X className="w-4 h-4" /> CANNOT DO
                    </h5>
                    <ul className="space-y-1">
                      {previewRole.cannotDo.map((item, idx) => (
                        <li key={idx} className="text-sm text-slate-500 flex items-start gap-2">
                          <span className="text-red-400 mt-1">•</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* Warning for PM */}
                {previewRole.requiresProjectAssignment && (
                  <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-xs text-amber-800 flex items-start gap-2">
                      <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      Access is limited to assigned projects only. Select at least one project.
                    </p>
                  </div>
                )}
                
                {/* Multiple roles indicator */}
                {selectedRoles.length > 1 && (
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-xs text-blue-800">
                      <strong>{selectedRoles.length} roles selected.</strong> This user will have combined permissions from all selected roles.
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center text-slate-400">
                  <UserPlus className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">Select a role to see permissions</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default InviteTeamMemberDialog;
